import { ReclamationView } from "src/api/generated/erp/db/types/tables/reclamationView.ts";
import { AsyncMenuButton } from "src/components/common/contextMenu/AsyncMenuButton.tsx";
import i18n from "i18next";
import { faBadgeCheck, faCheck, faLink, faPrint, faShare, faUndo } from "@fortawesome/pro-regular-svg-icons";
import { releaseReclamation } from "src/components/views/erp/reclamation/reclamationUtils.tsx";
import { useFormInput } from "src/components/common/dialogs/formInput/useFormInput.tsx";
import { ReclamationOperationApi } from "src/api/generated/erp/reclamations/api/reclamationOperationApi.ts";
import { downloadFile } from "src/utils/fileDownloading.ts";
import { useInputDialog } from "src/components/common/dialogs/input/useInputDialog.tsx";
import {
	InputDialogType,
	InputDialogValueType,
	ShowInputDialogParams,
} from "src/components/common/dialogs/input/types.ts";
import { CustomerOrderQueryApi } from "src/api/generated/erp/sales/customerOrder/api/customerOrderQueryApi.ts";
import { OpenCustomerOrderButton } from "src/components/views/erp/utilComponents/OpenCustomerOrderButton.tsx";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";

export interface ReclamationContextMenuProps {
	row: ReclamationView;
	allSelectedRows: ReclamationView[];
	refreshData: () => Promise<unknown>;
	customerOrderId?: number;
	onReclamationChanged?: (event: ReclamationChangedEvent) => void;
}

export interface ReclamationChangedEvent {
	type:
		| "released"
		| "ready_for_review"
		| "ready_for_review_reverted"
		| "closed"
		| "close_reverted"
		| "customer_orders_changed";
	rows: ReclamationView[];
}

export const ReclamationContextMenu = ({
	row,
	allSelectedRows,
	refreshData,
	customerOrderId,
	onReclamationChanged,
}: ReclamationContextMenuProps) => {
	const showFormInput = useFormInput();
	const showInputDialog = useInputDialog();
	const hasPermission = useHasPermission();
	const canManageReclamations = hasPermission(Permissions.Reclamations.MANAGE);
	const canReportReclamationProgress = hasPermission(Permissions.Reclamations.REPORT_PROGRESS);

	const multipleRowsSelected = allSelectedRows.length > 1;
	const onlySingleRowSelected = allSelectedRows.length === 1;
	const targetRows = multipleRowsSelected ? allSelectedRows : [row];
	const targetReclamationIds = targetRows.map((targetRow) => targetRow.reclamationId);
	const allTargetRowsReleasable = targetRows.every(
		(targetRow) => targetRow.reclamationState === "INITIAL" || targetRow.reclamationState === "PLANNED",
	);
	const allTargetRowsReadyForReviewable = targetRows.every((targetRow) => targetRow.reclamationState === "RELEASED");
	const targetWorkerUserIdOptions = Array.from(
		new Set(targetRows.map((targetRow) => targetRow.workerUserId).filter((value) => value != null)),
	);
	const targetWorkerUserId = targetWorkerUserIdOptions.length === 1 ? (targetWorkerUserIdOptions[0] ?? null) : null;

	return [
		canManageReclamations && allTargetRowsReleasable && (
			<AsyncMenuButton
				key={"release"}
				label={i18n.t("release")}
				icon={faShare}
				onClick={async () => {
					await releaseReclamation({
						showFormInput,
						reclamationIds: targetReclamationIds,
						currentWorkerUserId: targetWorkerUserId,
						afterReleased: async () => {
							await refreshData();
							onReclamationChanged?.({
								type: "released",
								rows: targetRows,
							});
						},
					});
				}}
			/>
		),
		canReportReclamationProgress && allTargetRowsReadyForReviewable && (
			<AsyncMenuButton
				key={"markReadyForReview"}
				label={i18n.t("mark_ready_for_review")}
				icon={faBadgeCheck}
				onClick={async () => {
					await ReclamationOperationApi.markReclamationReadyForReview({
						reclamationId: targetReclamationIds.length === 1 ? targetReclamationIds[0] : null,
						reclamationIds: targetReclamationIds,
					});
					await refreshData();
					onReclamationChanged?.({
						type: "ready_for_review",
						rows: targetRows,
					});
				}}
			/>
		),
		canReportReclamationProgress && row.reclamationState === "READY_FOR_REVIEW" && onlySingleRowSelected && (
			<AsyncMenuButton
				key={"revertReadyForReview"}
				label={i18n.t("return_to_work_queue")}
				icon={faUndo}
				onClick={async () => {
					await ReclamationOperationApi.revertMarkReclamationReadyForReview({
						reclamationId: row.reclamationId,
					});
					await refreshData();
					onReclamationChanged?.({
						type: "ready_for_review_reverted",
						rows: [row],
					});
				}}
			/>
		),
		(row.reclamationState === "RELEASED" || row.reclamationState === "READY_FOR_REVIEW") &&
			canManageReclamations &&
			onlySingleRowSelected && (
				<AsyncMenuButton
					key={"close"}
					label={i18n.t("close")}
					icon={faCheck}
					onClick={async () => {
						await ReclamationOperationApi.closeReclamation({
							reclamationId: row.reclamationId,
						});
						await refreshData();
						onReclamationChanged?.({
							type: "closed",
							rows: [row],
						});
					}}
				/>
			),
		row.reclamationState === "CLOSED" && canManageReclamations && onlySingleRowSelected && (
			<AsyncMenuButton
				key={"revert_close"}
				label={i18n.t("revert_close")}
				icon={faUndo}
				onClick={async () => {
					await ReclamationOperationApi.revertCloseReclamation({
						reclamationId: row.reclamationId,
					});
					await refreshData();
					onReclamationChanged?.({
						type: "close_reverted",
						rows: [row],
					});
				}}
			/>
		),
		onlySingleRowSelected && (
			<AsyncMenuButton
				key={"print"}
				label={i18n.t("print")}
				icon={faPrint}
				onClick={async () => {
					const fileHandle = await ReclamationOperationApi.printReclamation({
						reclamationId: row.reclamationId,
					});
					downloadFile(fileHandle);
				}}
			/>
		),
		canManageReclamations && onlySingleRowSelected && (
			<AsyncMenuButton
				key={"connectToCustomerOrder"}
				label={i18n.t("connect_to_customer_order")}
				icon={faLink}
				onClick={async () => {
					const selectedCustomerOrderId = await selectCustomerOrderToConnect(showInputDialog);
					if (selectedCustomerOrderId == undefined) return;
					await ReclamationOperationApi.connectToCustomerOrder({
						reclamationId: row.reclamationId,
						customerOrderId: selectedCustomerOrderId,
					});
					await refreshData();
					onReclamationChanged?.({
						type: "customer_orders_changed",
						rows: [row],
					});
				}}
			/>
		),
		onlySingleRowSelected &&
			customerOrderId == null &&
			row.customerOrderId != null &&
			hasPermission(Permissions.Sales.CustomerOrders.READ) && (
				<OpenCustomerOrderButton key={"openCustomerOrder"} customerOrderId={row.customerOrderId} />
			),
	];
};

function selectCustomerOrderToConnect(
	showInputDialog: <T extends InputDialogType>(
		params: ShowInputDialogParams<T>,
	) => Promise<InputDialogValueType<T> | undefined>,
) {
	return showInputDialog({
		defaultValue: null,
		type: "singleNumberSelect",
		title: i18n.t("connect_to_customer_order"),
		fieldLabel: i18n.t("customer_order"),
		fieldProps: {
			selection: {
				options: async ({ searchQuery, currentSelection }) => {
					const customerOrders = await CustomerOrderQueryApi.getCustomerOrderOptions({
						searchQuery,
						currentSelection,
					});
					return customerOrders.map((customerOrder) => ({
						value: customerOrder.customerOrderId,
						label: `${customerOrder.customerOrderId} | ${customerOrder.orderReference}`,
					}));
				},
			},
		},
	});
}
